import { FunctionComponent, ReactElement, ReactNode } from 'react'

import styled, { css } from 'styled-components'

import { baseColors } from '../primitives/colors'

import { Spinner } from './Spinner'

import { Text } from './Text'

const buttonStyles = {
  primary: {
    hasBorders: true,
    default: {
      background: baseColors.backgroundPrimaryAction,
      color: baseColors.contentInverseContent,
      borderColor: baseColors.backgroundPrimaryAction,
    },
    hover: {
      color: baseColors.contentInverseContent,
      background: baseColors.statesHoverBackground,
      borderColor: baseColors.backgroundPrimaryAction,
    },
    disabled: {
      background: baseColors.statesDisabledBackground,
      color: baseColors.contentDisabled,
      borderColor: undefined,
    },
    focus: {
      color: baseColors.contentInverseContent,
      outlineColor: baseColors.statesFocus,
    },
  },
  secondary: {
    hasBorders: true,
    default: {
      background: baseColors.backgroundSecondaryAction,
      color: baseColors.contentContent,
      borderColor: baseColors.borderDefault,
    },
    hover: {
      color: baseColors.contentInverseContent,
      background: baseColors.statesHoverBackground,
      borderColor: baseColors.backgroundPrimaryAction,
    },
    disabled: {
      background: baseColors.statesDisabledBackground,
      color: baseColors.contentDisabled,
      borderColor: undefined,
    },
    focus: {
      color: baseColors.contentContent,
      outlineColor: baseColors.statesFocus,
    },
  },

  tertiary: {
    hasBorders: true,
    default: {
      background: baseColors.backgroundCard,
      color: baseColors.contentContent,
      borderColor: baseColors.borderBackground,
    },
    hover: {
      color: baseColors.contentInverseContent,
      background: baseColors.statesHoverBackground,
      borderColor: baseColors.backgroundPrimaryAction,
    },
    disabled: {
      background: baseColors.statesDisabledBackground,
      color: baseColors.contentDisabled,
      borderColor: undefined,
    },
    focus: {
      color: baseColors.contentContent,
      outlineColor: baseColors.statesFocus,
    },
  },
}

const StyledButton = styled.button<{
  variant: Variant
  hasMinWidth: boolean
  fullWidth: boolean
  hasIcons: boolean
}>`
  height: fit-content;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: ${({ hasIcons }) => (hasIcons ? 'space-between' : 'center')};
  padding: 12px 24px;
  ${({ hasMinWidth }) =>
    hasMinWidth &&
    css`
      min-width: 168px;
    `}
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
  ${({ variant }) => {
    const styles = buttonStyles[variant]
    const { hasBorders } = styles

    return css`
      background-color: ${styles.default.background};
      color: ${styles.default.color};
      ${hasBorders &&
      css`
        border-top: 1px ${styles.default.borderColor} solid;
        border-right: 1px ${styles.default.borderColor} solid;
        border-bottom: 3px ${styles.default.borderColor} solid;
        border-left: 1px ${styles.default.borderColor} solid;
      `}

      &:hover {
        color: ${styles.hover.color};
        background-color: ${styles.hover.background};
        ${hasBorders &&
        css`
          border-top: 1px ${styles.hover.borderColor} solid;
          border-right: 1px ${styles.hover.borderColor} solid;
          border-bottom: 3px ${styles.hover.borderColor} solid;
          border-left: 1px ${styles.hover.borderColor} solid;
        `}
      }
      &:focus-visible {
        outline: 3px solid ${styles.focus.outlineColor};
        outline-offset: 2px;
        color: ${styles.focus.color};
      }
      &:disabled {
        background-color: ${styles.disabled.background};
        color: ${styles.disabled.color};
        box-shadow: none;
      }
    `
  }};
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
`

const LeftIconContainer = styled(IconContainer)`
  margin-right: 16px;
`

const RightIconContainer = styled(IconContainer)`
  margin-left: 16px;
`

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
`

export type Variant = 'primary' | 'secondary' | 'tertiary'

export interface ButtonProps {
  children: string | ReactElement
  onClick?: () => void
  variant?: Variant
  className?: string
  iconOnly?: ReactNode
  iconLeft?: ReactNode
  iconRight?: ReactNode
  disabled?: boolean
  hasMinWidth?: boolean
  fullWidth?: boolean
  loading?: boolean
  ariaLabel?: string
  [key: string]: any
}

export const Button: FunctionComponent<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  className,
  iconOnly,
  iconLeft,
  iconRight,
  disabled,
  hasMinWidth = false,
  fullWidth = false,
  loading,
  ariaLabel,
  ...restOfProperties
}) => (
  <StyledButton
    className={className}
    variant={variant}
    onClick={onClick}
    hasMinWidth={hasMinWidth}
    fullWidth={fullWidth}
    disabled={disabled || loading}
    hasIcons={!!iconLeft || !!iconRight}
    aria-label={typeof children === 'string' ? children : ariaLabel}
    {...restOfProperties}
  >
    {iconOnly &&
      (loading ? (
        <SpinnerContainer>
          <Spinner size="sm" color={buttonStyles[variant].disabled.color} />
        </SpinnerContainer>
      ) : (
        iconOnly
      ))}
    {!iconOnly && (
      <>
        {iconLeft && <LeftIconContainer>{iconLeft}</LeftIconContainer>}
        {loading ? (
          <SpinnerContainer>
            <Spinner size="sm" color={buttonStyles[variant].disabled.color} />
          </SpinnerContainer>
        ) : (
          <Text element="span" type="body" size="sm" fontWeight={600}>
            {children}
          </Text>
        )}
        {iconRight && <RightIconContainer>{iconRight}</RightIconContainer>}
      </>
    )}
  </StyledButton>
)
